// =====================================================================
// CommandePrete.jsx — Commandes prêtes (13-10-2026, v2 multi-client 16-10-2026, v3 vue d'ensemble
// 16-10-2026) — page d'entrée du module
// =====================================================================
// v3 (SPEC-v3 A1) : la page devient la VUE D'ENSEMBLE, pour tous les clients :
//  - filtres Client (ChoixClient + « Tous les clients ») et Jour (Aujourd'hui / Hier / 7 derniers jours) ;
//  - 4 compteurs cliquables = 4 vues — Envoyées, Brouillons, Encore à quai (vue par défaut), Chargées
//    (logique PURE : ./commande-prete/vue.js, testée) ; temps à quai « 2 j 4 h » ;
//  - « Supprimer » sur les brouillons que le serveur dit supprimables (brouillon jamais envoyé, ou
//    commande de test), confirmation SUR LA PAGE (16-10-2026) ;
//  - admins : « Réessayer la facturation » sur une commande dont la facturation automatique a échoué.
// Boutons « ＋ Nouvelle commande » (le formulaire d'avant, INCHANGÉ : « Client » puis « Réf. chargement
// (ODP) — scanner ou taper » + Entrée → crée la commande de ce client et de cette réf. ou rouvre
// l'existante, puis ouvre l'écran PDA /commande-prete/:id ; case « Commande de test » — ADMINISTRATEURS
// seulement, décision Silviu 17/10), « 🚚 Chargement » et (admins + coordinateurs) « ⚙ Paramètres clients ».
// Supprimer une commande de test : administrateurs seulement (17/10).
// Droits PROPRES au module (SPEC v3 D, 17/10 — ./commande-prete/droits.js) : page = « Voir » ; « ＋ Nouvelle
// commande » et « Supprimer » = « Encoder et envoyer » ; « 🚚 Chargement » = « Charger un camion » ; « Vu » /
// « Marquer imprimé » = « Imprimer les documents » ; Paramètres clients, case test, facturation = admin.
// =====================================================================

import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { readyOrders, errMsg } from '../services/api.js';
import { useAuth } from '../hooks/useAuth.jsx';
import Breadcrumbs from '../components/Breadcrumbs.jsx';
import ChoixClient from './commande-prete/ChoixClient.jsx';
import BadgeTest from './commande-prete/BadgeTest.jsx';
import { RdvDocsAFaire } from './commande-prete/RdvDocsZeus.jsx';   // v3 B (16-10-2026)
import TempsEncodage from './commande-prete/TempsEncodage.jsx';   // 27-10-2026 : à droite, admin seulement
import SurDalle from './commande-prete/SurDalle.jsx';             // 27-10-2026 : à gauche, admin seulement
import { useClientsCommandesPretes } from './commande-prete/useClients.js';
import { normRefChargement, verifRefChargement, refInhabituelle } from './commande-prete/regles.js';
import { extraireDto } from './commande-prete/synchro.js';
import {
  texteRegles, lireChoixTest, texteChoixTest, libelleVraieCommande, idVraieCommande, choixPourSaisie,
} from './commande-prete/clients.js';
import { dateHeureBxl } from './commande-prete/dates.js';
import {
  peutSupprimer, libelleSuppression, texteConfirmationSuppression, texteSupprimee, texteRefusSuppression,
} from './commande-prete/suppression.js';
import { useGardeFocus } from './commande-prete/useGardeFocus.js';
import { droitsCommandesPretes, texteAccesRefuse, DROIT_LIRE } from './commande-prete/droits.js';
import {
  VUES, VUE_DEFAUT, JOURS, JOUR_DEFAUT, TOUS_LES_CLIENTS, lireJour, paramsVue, libelleCompteur, explication, texteVide,
  texteDuree, texteUnitesVue, texteReste, etatVue, statutBrouillon, facturationAReprendre, texteFacturation, texteEnvoi, alerteEnvoi,
  lireReponseVue, retirerLigne, rdvVue,
} from './commande-prete/vue.js';

const inp = {
  width: '100%', padding: '0.6rem 0.7rem', border: '1px solid #cbd5e1', borderRadius: 8,
  fontSize: 16, minHeight: 44, boxSizing: 'border-box', background: '#fff', fontFamily: 'monospace',
};
const lienBouton = {
  display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 44, padding: '0 14px', borderRadius: 8,
  border: '1px solid #cbd5e1', background: '#fff', color: '#0f6e74', fontWeight: 700, textDecoration: 'none', fontSize: '0.95rem', cursor: 'pointer',
};
const petitBouton = {
  minHeight: 40, padding: '0 12px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', color: '#334155',
  fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
};
const boutonDanger = { ...petitBouton, border: '1px solid #a6342b', background: '#f8dedb', color: '#a6342b' };
// Pastilles (couleurs des badges de Dimensions)
const PASTILLES = {
  quai: { bg: '#FEF3C7', fg: '#92400E' }, part: { bg: '#DBEAFE', fg: '#1E40AF' }, ok: { bg: '#D1FAE5', fg: '#065F46' },
  draft: { bg: '#F1F5F9', fg: '#475569' }, test: { bg: '#FDE7DF', fg: '#7c2d12' }, warn: { bg: '#FEE2E2', fg: '#991B1B' },
};
const Pastille = ({ p }) => {
  const c = PASTILLES[p.cls] || PASTILLES.draft;
  return <span title={p.titre || undefined} style={{ background: c.bg, color: c.fg, borderRadius: 999, padding: '2px 10px', fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{p.txt}</span>;
};

// Détail (2e ligne) d'une commande selon la vue.
function detail(vue, l) {
  const t = [];
  if (vue === 'brouillons') t.push(`créée le ${dateHeureBxl(l.creeLe)}`);
  else if (vue === 'chargees') {
    t.push(`dernier scan ${dateHeureBxl(l.dernierChargement)}`);
    if (l.dureeQuaiMinutes !== null && l.dureeQuaiMinutes !== undefined) t.push(l.aQuaiJusqua ? `${texteDuree(l.dureeQuaiMinutes)} à quai` : `encore à quai (${texteReste(l)}) depuis ${texteDuree(l.dureeQuaiMinutes)}`);
  } else {
    t.push(texteEnvoi(l));
    const duree = l.dureeQuaiMinutes !== null && l.dureeQuaiMinutes !== undefined;
    if (vue === 'quai') { t.push(`reste à quai ${texteReste(l)}`); if (duree) t.push(`à quai depuis ${texteDuree(l.dureeQuaiMinutes)}`); }
    else if (duree && l.unites && l.charges < l.unites) t.push(`à quai depuis ${texteDuree(l.dureeQuaiMinutes)}`);
    else if (duree && l.aQuaiJusqua) t.push(`${texteDuree(l.dureeQuaiMinutes)} à quai`);
  }
  // Rendez-vous d'enlèvement (pris par le client sur la page publique) : jour, créneau, société
  const rdv = vue === 'brouillons' ? null : rdvVue(l);
  if (rdv && rdv.detail) t.push(rdv.detail);
  return t;
}

export default function CommandePrete() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Suppression : ligne dont la question est ouverte, suppression en cours, message (fait / refusé).
  // (Une commande supprimée depuis son écran arrive ici avec son message.)
  const [aSupprimer, setASupprimer] = useState(null);
  const [suppression, setSuppression] = useState(() => (location.state?.supprimee ? { ok: true, txt: location.state.supprimee } : null));
  const [supprEnCours, setSupprEnCours] = useState(false);
  // Nouvelle commande (formulaire d'avant, ouvert par « ＋ Nouvelle commande »)
  const [creation, setCreation] = useState(false);
  const [client, setClient] = useState(null);
  const [test, setTest] = useState(false);
  const [saisie, setSaisie] = useState('');
  // (revue 17/10) La réf. a aussi une commande de TEST : rien n'est créé, on choisit (essai / vraie commande).
  // La question porte la réf. et le client qui l'ont produite : affichée seulement s'ils sont encore à l'écran.
  const [choixRecu, setChoixRecu] = useState(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const champ = useRef(null);
  const champClient = useRef(null);
  // Vue d'ensemble
  const [filtreClient, setFiltreClient] = useState(TOUS_LES_CLIENTS);
  const [jour, setJour] = useState(JOUR_DEFAUT);
  const [vue, setVue] = useState(VUE_DEFAUT);
  const [data, setData] = useState(null);
  const [erreurVue, setErreurVue] = useState('');
  const [facture, setFacture] = useState({});   // id → { enCours } | { ok, txt }
  const seq = useRef(0);
  const droits = droitsCommandesPretes(can);
  const autorise = droits.lire;
  const encoder = droits.encoder;   // « ＋ Nouvelle commande », « Supprimer le brouillon »
  const admin = droits.admin;
  // « Commande de test » : case pour les administrateurs seulement (décision Silviu 17/10) ; sans elle, jamais
  // test (droit retiré pendant que la page était ouverte compris).
  const testCoche = admin && test;
  const choixTest = testCoche ? null : choixPourSaisie(choixRecu, normRefChargement(saisie), client?.id);
  const { clients, erreur: erreurClients } = useClientsCommandesPretes(autorise);
  // Champ Client du filtre : « Tous les clients » est un choix de la liste ; pendant la frappe, le filtre en
  // vigueur reste (voir onChoix plus bas).
  const clientsFiltre = clients ? [TOUS_LES_CLIENTS, ...clients] : clients;
  const idFiltre = filtreClient && filtreClient.id ? filtreClient.id : '';

  // La dernière demande seulement s'affiche (filtre changé pendant qu'une réponse arrivait) ; toujours avec
  // le filtre AFFICHÉ (une relecture après « Réessayer » / « Supprimer » lit le filtre courant, pas celui du clic).
  const filtres = useRef({ client: filtreClient, jour });
  filtres.current = { client: filtreClient, jour };
  const relireVue = () => {
    const n = ++seq.current;
    return readyOrders.vue(paramsVue(filtres.current))
      .then((r) => { if (n === seq.current) { setData(lireReponseVue(r)); setErreurVue(''); } })
      .catch((e) => { if (n === seq.current) setErreurVue(errMsg(e)); });
  };
  useEffect(() => {
    if (!autorise) return;
    // Filtre changé : la vue de l'ancien filtre ne reste jamais affichée sous le nouveau (« Chargement… »)
    setData(null);
    setErreurVue('');
    relireVue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autorise, idFiltre, jour]);
  // Message « supprimée » venu de l'écran de la commande : montré une fois (pas à chaque rechargement).
  useEffect(() => {
    if (location.state?.supprimee) navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // (revue 17/10, 9e passe) Question « Supprimer ? » ouverte sur une ligne qui ne l'est plus pour cette personne
  // (devenue un essai, disparue de la vue…) : la question est FERMÉE — elle ne revient jamais toute seule. (14e
  // passe) Vue en cours de rechargement (`data` null : Jour ou Client changé) : fermée aussi. (Le focus, s'il était
  // sur un de ses boutons : le garde ci-dessous.)
  useEffect(() => {
    if (!aSupprimer) return;
    const l = data ? (data[vue] || []).find((x) => x.id === aSupprimer) : null;
    if (!l || !peutSupprimer(l, admin, encoder)) setASupprimer(null);
  }, [data, vue, aSupprimer, admin, encoder]);
  // (revue 17/10, 12e-14e passes) Garde du focus (useGardeFocus, au niveau du document) : l'élément focalisé de la
  // page vient de disparaître ou d'être désactivé (question « Supprimer ? » fermée — « Annuler », refus, plus
  // permise —, choix essai / vraie commande répondu, ligne retirée, « ✓ Vu » / « Marquer imprimé »…) et le focus est
  // tombé → le champ réf. du formulaire « Nouvelle commande » SEULEMENT s'il est ouvert, actif et pas en attente
  // (busy), sans défilement ni select() (pas de clavier ouvert pour rien) ; sinon rien.
  const refGarde = useGardeFocus(() => {
    const c = champ.current;
    if (creation && c && !c.disabled && !busy) c.focus({ preventScroll: true });
  });

  // « Supprimer » : question sur la page, focus sur « Annuler » (jamais sur « Oui, supprimer » :
  // une Entrée de scanner ne supprime rien).
  const demanderSuppression = (o) => {
    setASupprimer(o.id);
    setSuppression(null);
    setTimeout(() => document.getElementById(`supprNon-${o.id}`)?.focus(), 0);
  };
  const supprimer = async (o) => {
    if (supprEnCours) return;
    setSupprEnCours(true);
    try {
      await readyOrders.remove(o.id);
      setData((d) => retirerLigne(d, o.id));
      setSuppression({ ok: true, txt: texteSupprimee(o) });
      relireVue();   // (une vue relue pendant la suppression, filtre changé, la contenait encore)
    } catch (e) {
      setSuppression({ ok: false, txt: `${o.orderRef} : ${texteRefusSuppression(e, errMsg(e))}` });
      relireVue();   // refusée (envoyée entre-temps…), disparue ou sans réponse : la vue dit l'état réel
    } finally {
      setASupprimer(null);
      setSupprEnCours(false);
    }
  };

  // Admin : reprise de la facturation automatique d'une commande.
  const reessayer = async (l) => {
    if (facture[l.id]?.enCours) return;
    setFacture((f) => ({ ...f, [l.id]: { enCours: true } }));
    try {
      const r = await readyOrders.reessayerFacturation(l.id);
      const av = Array.isArray(r?.avertissements) ? r.avertissements : [];
      setFacture((f) => ({ ...f, [l.id]: av.length ? { ok: false, txt: av.join(' · ') } : { ok: true, txt: 'Facturation faite.' } }));
    } catch (e) {
      setFacture((f) => ({ ...f, [l.id]: { ok: false, txt: errMsg(e) } }));
    }
    relireVue();
  };

  // Client choisi → le curseur passe à la réf. (ChoixClient, prop `suivant`, après le rendu qui la dégrise).
  const choisirClient = (c) => { setClient(c); setMsg(''); setChoixRecu(null); };
  // Une réf. scannée dans le champ Client par erreur : on le dit clairement.
  const aideAucun = (t) => (!verifRefChargement(t) && !refInhabituelle(t)
    ? `« ${normRefChargement(t)} » est une réf. de chargement : choisissez d'abord le client`
    : null);

  // Entrée (fin de scan) : la commande est créée ou rouverte — jamais deux fois la même.
  // `choix` ('essai' | 'reelle' | 'reelle-existante', seulement depuis la question) : la réponse quand la réf. a
  // aussi une commande de TEST — c'est le SERVEUR qui retrouve l'essai ou la vraie commande de cette réf. chez ce
  // client, lus au moment même (revue 17/10, 7e passe : aucun identifiant de l'écran, aucune question périmée possible).
  const ouvrir = async (choix = null) => {
    if (busy) return;
    if (!client) { setMsg('Choisissez d\'abord le client'); champClient.current?.focus(); return; }
    const m = verifRefChargement(saisie);
    if (m) { setMsg(m); champ.current?.select(); return; }
    setBusy(true);
    setMsg('');
    setChoixRecu(null);
    const ref = normRefChargement(saisie);
    const clientId = client.id;
    try {
      const r = await readyOrders.create(ref, clientId, testCoche, testCoche ? null : choix);
      const dto = extraireDto(r) || r?.dto || r;
      if (!dto || !dto.id) throw new Error('Réponse inattendue du serveur');
      // Avertissements de la création (ex. commande existante ouverte TELLE QUELLE alors que la case
      // « test » ne correspond pas) : transmis à l'écran de la commande, qui les affiche.
      navigate(`/commande-prete/${dto.id}`, {
        state: { existing: !!dto.existing, avertissements: Array.isArray(dto.avertissements) ? dto.avertissements : [], testDemande: choix ? null : testCoche },   // (choix fait : rien à comparer à la case)
      });
    } catch (e) {
      // Rien de créé : la question s'affiche sous la réf. — pour CETTE réf. et CE client seulement.
      const c = lireChoixTest(e?.response?.data);
      if (c) setChoixRecu({ ...c, ref, clientId });
      else setMsg(errMsg(e));
      setBusy(false);
      // Le champ réf. reprend la main, sélectionné : un nouveau scan REMPLACE la réf. (jamais « BROUT1BROUT1 ») —
      // focalisé aussi quand la requête venait d'un bouton de la question (parti avec elle) ; jamais pris à un autre
      // champ où l'on tape entre-temps.
      const champRef = champ.current, actif = document.activeElement;
      if (champRef && (!actif || actif === document.body || actif === champRef)) { champRef.focus({ preventScroll: true }); champRef.select(); }
    }
  };
  // Réponses à la question (affichée seulement pour la réf. et le client à l'écran — choixPourSaisie) :
  // l'essai de cette réf. (rouvert ; disparu entre-temps → 409 ESSAI_ABSENT, le message le dit, rien d'ouvert),
  // ou la vraie commande (rouverte, ou créée : son mail partira au client).
  const ouvrirEssai = () => { if (choixTest) ouvrir('essai'); };
  // (10e revue) Elle existait à la question → la ROUVRIR seulement (disparue : 409 REELLE_ABSENTE, rien recréé).
  const ouvrirReelle = () => { if (choixTest) ouvrir(idVraieCommande(choixTest) ? 'reelle-existante' : 'reelle'); };

  if (!autorise) {
    return <div style={{ maxWidth: 640, margin: '0 auto', padding: '0.75rem', color: '#991B1B' }}>{texteAccesRefuse(DROIT_LIRE)}</div>;
  }

  const lignes = data ? data[vue] : null;
  return (
    // 27-10-2026 : avec « Administrer les commandes prêtes », le panneau « Temps d'encodage » s'ajoute À DROITE ;
    // sur un écran étroit (PDA) il passe sous la liste. Sans ce droit, la page reste exactement comme avant.
    // 27-10-2026 : trois colonnes sur grand écran (« Sur dalle » à gauche, la liste, « Temps d'encodage » à droite) ;
    // plus étroit, les panneaux passent SOUS la liste (ordre du document : la liste d'abord).
    <div ref={refGarde} style={admin ? { maxWidth: 1900, margin: '0 auto', display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'center', gap: 16 } : undefined}>
    {admin && <style>{'.cp-dalle { order: 1; flex-basis: 100% !important; } @media (min-width: 1840px) { .cp-dalle { order: -1; flex-basis: 470px !important; } }'}</style>}
    <div style={{ maxWidth: 900, margin: admin ? 0 : '0 auto', padding: '0.75rem', ...(admin ? { flex: '1 1 560px', minWidth: 0 } : {}) }}>
      <Breadcrumbs items={[{ label: 'Commandes prêtes' }]} />
      <h1 style={{ fontSize: '1.25rem', margin: '0.25rem 0 0.25rem' }}>📦 Commandes prêtes</h1>
      <p style={{ margin: '0 0 0.6rem', color: '#475569', fontSize: '0.9rem' }}>
        Palettes et colis mesurés + photos, puis mail « commande prête » au client.
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        {encoder && (
          <button type="button" style={{ ...lienBouton, ...(creation ? { background: '#e2eff2' } : {}) }} id="lienNouvelle" aria-expanded={creation}
            onClick={() => { setCreation((v) => !v); setChoixRecu(null); }}>＋ Nouvelle commande</button>
        )}
        {droits.charger && <Link to="/commande-prete/chargement" style={lienBouton} id="lienChargement">🚚 Chargement</Link>}
        {admin && <Link to="/commande-prete/clients" style={lienBouton} id="lienParametres">⚙ Paramètres clients</Link>}
      </div>
      {/* v3 B (16-10-2026) : rendez-vous pas encore vus + documents à imprimer, toutes commandes (rien sinon).
          Boutons « Vu » / « Marquer imprimé » : droit « Imprimer les documents » (SPEC v3 D). */}
      <RdvDocsAFaire imprimer={droits.imprimer} />

      {creation && encoder && (
        <div id="carteNouvelle" style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '0.9rem', marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <ChoixClient id="cp-client" inputRef={champClient} clients={clients} erreurListe={erreurClients}
              valeur={client} onChoix={choisirClient} autoFocus aideAucun={aideAucun} suivant={() => champ.current} />
            {client && <div style={{ marginTop: 4, fontSize: '0.85rem', color: testCoche ? '#7c2d12' : '#2a7034', fontWeight: 600, overflowWrap: 'anywhere' }}>{texteRegles(client, testCoche)}</div>}
          </div>
          <div>
            <label htmlFor="cp-nouvelle" style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#5b6b73', marginBottom: 4 }}>
              Réf. chargement (ODP) — scanner ou taper
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 8 }}>
              <input id="cp-nouvelle" ref={champ} autoComplete="off" autoCapitalize="characters" spellCheck={false} value={saisie}
                placeholder={client ? 'ex. BROUT117200' : 'Choisissez d\'abord le client'} disabled={!client}
                // (14e revue) Requête en cours (Entrée, ou réponse à la question essai / vraie commande) : lecture seule —
                // un scan pendant l'attente ne s'y écrit pas et est ignoré sans message (Entrée : `busy`) ; le focus reste.
                readOnly={busy}
                onChange={(e) => { setSaisie(e.target.value); setMsg(''); setChoixRecu(null); }}
                onFocus={(e) => e.target.select()}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); ouvrir(); } }}
                style={{ ...inp, ...(client ? {} : { background: '#e9eef0', cursor: 'not-allowed' }) }} />
              <button type="button" onClick={() => ouvrir()} disabled={busy || !client}
                style={{ minHeight: 44, padding: '0 16px', fontSize: '1rem', fontWeight: 700, background: '#0f6e74', color: '#fff', border: 0, borderRadius: 8, cursor: busy ? 'wait' : client ? 'pointer' : 'not-allowed', opacity: client ? 1 : 0.45 }}>
                {busy ? '…' : 'Ouvrir'}
              </button>
            </div>
            {/* (L'avertissement « Réf. inhabituelle (attendu BROUT…) », non bloquant, s'affiche sur l'écran
                de la commande une fois la réf. validée — jamais ici pendant la frappe.) */}
            {msg && <div role="alert" style={{ marginTop: 6, fontSize: '0.875rem', color: '#a6342b' }}>{msg}</div>}
            {/* (revue 17/10) La réf. a aussi une commande de TEST : rien n'est créé avant ce choix. */}
            {choixTest && (
              <div role="alert" id="cp-choix-test" style={{ marginTop: 8, padding: '10px 12px', border: '1px solid #f59e0b', background: '#fffbeb', borderRadius: 8, display: 'grid', gap: 8 }}>
                <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#7c2d12', overflowWrap: 'anywhere' }}>
                  {texteChoixTest(choixTest, dateHeureBxl(choixTest.essai.creeLe, { annee: true }))}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" id="cp-ouvrir-essai" onClick={ouvrirEssai} disabled={busy}
                    style={{ minHeight: 44, padding: '0 14px', fontSize: '0.95rem', fontWeight: 700, background: '#fff', color: '#7c2d12', border: '1px solid #f59e0b', borderRadius: 8, cursor: 'pointer' }}>
                    🧪 Ouvrir la commande de test
                  </button>
                  <button type="button" id="cp-ouvrir-reelle" onClick={ouvrirReelle} disabled={busy}
                    style={{ minHeight: 44, padding: '0 14px', fontSize: '0.95rem', fontWeight: 700, background: '#0f6e74', color: '#fff', border: 0, borderRadius: 8, cursor: busy ? 'wait' : 'pointer' }}>
                    {libelleVraieCommande(choixTest)}
                  </button>
                </div>
              </div>
            )}
          </div>
          {admin && (
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: '0.9rem', cursor: 'pointer', minHeight: 32 }}>
              <input type="checkbox" id="cp-test" checked={test} onChange={(e) => { setTest(e.target.checked); setChoixRecu(null); }} style={{ width: 22, height: 22, marginTop: 1, flex: '0 0 auto' }} />
              <span><b>Commande de test</b> — mail seulement à l'adresse de test (sujet [TEST]), rien vers EffiTrace, invisible pour le client.</span>
            </label>
          )}
          <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
            Une commande par client et par réf. de chargement : déjà commencée, elle est rouverte, jamais créée deux fois.
          </div>
        </div>
      )}

      {/* ---------- Vue d'ensemble ---------- */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '0.9rem', marginBottom: 10, display: 'grid', gap: 10 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 240px', minWidth: 0 }}>
            {/* Pendant la frappe (choix retiré : null), le filtre en vigueur reste — rien n'est relu avant un
                vrai choix ; quitté sans choix, le champ revient au filtre en vigueur (ChoixClient). */}
            <ChoixClient id="cp-filtre-client" label="Client" clients={clientsFiltre} erreurListe={erreurClients}
              valeur={filtreClient} onChoix={(c) => { if (c) setFiltreClient(c); }} />
          </div>
          <div style={{ flex: '0 1 200px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label htmlFor="cp-jour" style={{ fontSize: '0.8rem', fontWeight: 700, color: '#5b6b73' }}>Jour</label>
            <select id="cp-jour" value={jour} onChange={(e) => setJour(lireJour(e.target.value))}
              style={{ font: 'inherit', fontSize: 16, minHeight: 44, borderRadius: 8, border: '1px solid #cfd8dc', padding: '0 10px', background: '#fff', width: '100%' }}>
              {JOURS.map((j) => <option key={j.v} value={j.v}>{j.txt}</option>)}
            </select>
          </div>
        </div>
        <div role="group" aria-label="Vues" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
          {VUES.map((v) => {
            const n = data ? data.compteurs[v] : null;
            const actif = v === vue;
            return (
              <button key={v} type="button" id={`compteur-${v}`} aria-pressed={actif} onClick={() => setVue(v)}
                style={{ display: 'grid', gap: 2, textAlign: 'left', padding: '10px 12px', borderRadius: 8, cursor: 'pointer', font: 'inherit', color: '#0f172a',
                  border: `1px solid ${actif ? '#0f6e74' : '#e2e8f0'}`, background: actif ? '#e2eff2' : '#f8fafc' }}>
                <b style={{ fontSize: '1.6rem', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{n === null ? '…' : n}</b>
                <span style={{ fontSize: '0.82rem', color: '#475569' }}>{libelleCompteur(v, n, jour)}</span>
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: '0.85rem', color: '#475569' }} id="cp-explication">{explication(vue)}</div>
      </div>

      {/* Admins : facturation automatique à reprendre, toutes dates (une commande sortie des 4 vues ne s'oublie jamais) */}
      {admin && data && data.aFacturer.length > 0 && (
        <div id="cp-a-facturer" role="status" style={{ marginBottom: 10, padding: '8px 10px', borderRadius: 8, background: '#FEF3C7', color: '#92400E', fontSize: '0.88rem', display: 'grid', gap: 6 }}>
          <b>{`Facturation automatique à reprendre : ${data.aFacturer.length} commande${data.aFacturer.length > 1 ? 's' : ''}`}</b>
          {data.aFacturer.map((l) => {
            const f = facture[l.id];
            return (
              <div key={l.id} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', overflowWrap: 'anywhere' }}>
                <Link to={`/commande-prete/${l.id}`} style={{ fontFamily: 'monospace', fontWeight: 700, color: '#92400E' }}>{l.orderRef}</Link>
                <span>{`${(l.client && l.client.name) || '—'} · ${(l.facturation.motifs || []).join(' · ')}`}</span>
                <button type="button" id={`afacturer-${l.id}`} style={petitBouton} disabled={!!f?.enCours} onClick={() => reessayer(l)}>
                  {f?.enCours ? '…' : '↻ Réessayer la facturation'}
                </button>
                {f && !f.enCours && <span style={{ color: f.ok ? '#2a7034' : '#a6342b' }}>{f.txt}</span>}
              </div>
            );
          })}
        </div>
      )}
      {suppression && (
        <div role="status" id="supprMessage"
          style={{ marginBottom: 10, padding: '8px 10px', borderRadius: 8, fontSize: '0.9rem', overflowWrap: 'anywhere', background: suppression.ok ? '#dcefdc' : '#f8dedb', color: suppression.ok ? '#2a7034' : '#a6342b' }}>
          {suppression.txt}
        </div>
      )}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
        {erreurVue && <div style={{ padding: '0.9rem', color: '#a6342b', fontSize: '0.9rem' }}>Vue indisponible : {erreurVue}</div>}
        {!erreurVue && lignes === null && <div style={{ padding: '0.9rem', color: '#64748b' }}>Chargement…</div>}
        {lignes && lignes.length === 0 && (
          <div style={{ padding: '1.25rem', textAlign: 'center', color: '#94a3b8' }}>{texteVide(vue, { client: filtreClient, jour })}</div>
        )}
        {lignes && data.compteurs[vue] > lignes.length && (
          <div style={{ padding: '6px 12px', fontSize: '0.8rem', color: '#92400E', background: '#FEF3C7' }}>{`${lignes.length} premières sur ${data.compteurs[vue]} : affinez avec le filtre Client.`}</div>
        )}
        {lignes && lignes.map((ligne) => {
          // (`status` : nom attendu par les textes de suppression, partagés avec l'écran de la commande)
          const l = { ...ligne, status: ligne.statut };
          const p = vue === 'brouillons' ? statutBrouillon(l) : etatVue(l);
          const alerte = vue === 'brouillons' ? null : alerteEnvoi(l);
          const rdv = vue === 'brouillons' ? null : rdvVue(l);
          const question = aSupprimer === l.id;
          const f = facture[l.id];
          const aReprendre = admin && facturationAReprendre(l);
          return (
            <div key={l.id} style={{ borderTop: '1px solid #F1F5F9' }}>
              {/* (PDA étroit : les boutons passent sous la ligne, décalés comme le texte) */}
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, ...(peutSupprimer(l, admin, encoder) || aReprendre ? { padding: '0 10px 6px 0' } : {}) }}>
                <Link to={`/commande-prete/${l.id}`}
                  style={{ flex: '1 1 260px', minWidth: 0, display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', padding: '10px 12px', minHeight: 52, textDecoration: 'none', color: '#0f172a' }}>
                  <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: 'monospace', fontWeight: 700, overflowWrap: 'anywhere' }}>{l.orderRef}</span>
                      {l.isTest && <BadgeTest />}
                    </span>
                    <span style={{ fontSize: '0.8rem', color: '#64748b', overflowWrap: 'anywhere' }}>
                      <b style={{ color: '#334155' }}>{(l.client && l.client.name) || '—'}</b> · {texteUnitesVue(l)} · {detail(vue, l).join(' · ')}
                    </span>
                  </span>
                  <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {alerte && <Pastille p={alerte} />}
                    {rdv && <Pastille p={rdv} />}
                    <Pastille p={p} />
                  </span>
                </Link>
                {peutSupprimer(l, admin, encoder) && !question && (
                  <button type="button" id={`suppr-${l.id}`} style={{ ...petitBouton, marginLeft: 12 }} disabled={supprEnCours}
                    onClick={() => demanderSuppression(l)}>🗑 {libelleSuppression(l)}</button>
                )}
                {aReprendre && (
                  <button type="button" id={`facturer-${l.id}`} style={{ ...petitBouton, marginLeft: 12 }} disabled={!!f?.enCours}
                    title={texteFacturation(l)} onClick={() => reessayer(l)}>{f?.enCours ? '…' : '↻ Réessayer la facturation'}</button>
                )}
              </div>
              {aReprendre && <div style={{ margin: '0 12px 8px', fontSize: '0.8rem', color: '#92400E', overflowWrap: 'anywhere' }}>⚠ {texteFacturation(l)}</div>}
              {f && !f.enCours && (
                <div role="status" style={{ margin: '0 12px 8px', fontSize: '0.8rem', color: f.ok ? '#2a7034' : '#a6342b', overflowWrap: 'anywhere' }}>{f.txt}</div>
              )}
              {question && peutSupprimer(l, admin, encoder) && (
                <div role="alert" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', margin: '0 12px 10px', padding: 8, borderRadius: 8, background: '#f8dedb', color: '#a6342b', fontSize: '0.88rem', fontWeight: 600 }}>
                  <span style={{ flex: '1 1 100%', overflowWrap: 'anywhere' }}>{texteConfirmationSuppression(l)}</span>
                  <button type="button" id={`supprOk-${l.id}`} style={boutonDanger} disabled={supprEnCours} onClick={() => supprimer(l)}>
                    {supprEnCours ? '…' : 'Oui, supprimer'}
                  </button>
                  <button type="button" id={`supprNon-${l.id}`} style={petitBouton} disabled={supprEnCours} onClick={() => setASupprimer(null)}>Annuler</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
    {admin && <div className="cp-dalle" style={{ flex: '0 1 470px', minWidth: 300, padding: '0.75rem', marginTop: 40 }}><SurDalle /></div>}
    {admin && <div style={{ flex: '0 1 480px', minWidth: 300, padding: '0.75rem', marginTop: 40 }}><TempsEncodage /></div>}
    </div>
  );
}
